import { useCallback, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useCastContext } from '../cast-context';
import { CastError } from '../cast-error';
import { CastSession, CastSessionStatus } from '../interfaces';
import {
  getCastStatus,
  getInitialCastStatus,
  getSession,
  removeLocalSessionId,
  setLocalSessionId,
} from '../utils/session';

export interface CastSessionResult {
  session?: CastSession;
  sessionStatus: CastSessionStatus;
}

export function useCastSession(): CastSessionResult {
  const { sender, context } = useCastContext();

  const [session, setSession] = useState<CastSession | undefined>(() => getSession(context));
  const [sessionStatus, setCastSessionStatus] = useState<CastSessionStatus>(() => getInitialCastStatus(session));
  const [error, setError] = useState<CastError | undefined>(undefined);

  const handleSessionChange = useCallback((event: cast.framework.SessionStateEventData) => {
    unstable_batchedUpdates(() => {
      setCastSessionStatus(getCastStatus(event.sessionState));
      setSession(event.session);

      switch (event.sessionState) {
        case 'SESSION_STARTED':
        case 'SESSION_RESUMED':
          setLocalSessionId(event.session.getSessionId());
          break;
        case 'SESSION_START_FAILED':
          if (event.errorCode !== 'cancel' && event.errorCode !== 'timeout') {
            setError(new CastError(event.errorCode, { sessionId: 'N/A' }));
          }
          break;
      }
    });
  }, []);

  useEffect(() => {
    if (sender && context) {
      context.addEventListener(sender.CastContextEventType.SESSION_STATE_CHANGED, handleSessionChange);

      return () => {
        context.removeEventListener(sender.CastContextEventType.SESSION_STATE_CHANGED, handleSessionChange);
      };
    }
  }, [handleSessionChange, sender, context]);

  const handleSessionMessage = useCallback(
    (_namespace: string, message: string) => {
      setError(new CastError(message, { sessionId: session ? session.getSessionId() : 'N/A' }));
      removeLocalSessionId();
    },
    [session],
  );

  useEffect(() => {
    if (session) {
      session.addMessageListener('urn:x-cast:com.google.cast.error', handleSessionMessage);

      return () => {
        session.removeMessageListener('urn:x-cast:com.google.cast.error', handleSessionMessage);
      };
    }
  }, [handleSessionMessage, session]);

  if (error) {
    throw error;
  }

  return {
    session,
    sessionStatus,
  } as const;
}
